import React, { useState } from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { frFR, enUS } from '@mui/x-date-pickers/locales';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';

import ReactPhoneInput from '../vendor/reactPhoneInput';
import { useGetMyProfileQuery, useUpdateMyProfileMutation, useChangePasswordMutation } from '../api/apiSlice';
import { MyProfile } from '../types/api';

function ProfileForm({ profile }: { profile: MyProfile }) {
    const { t, i18n } = useTranslation();
    const pickersLocaleText = (i18n.language === 'en' ? enUS : frFR).components.MuiLocalizationProvider.defaultProps.localeText;
    const [updateMyProfile] = useUpdateMyProfileMutation();
    const [changePassword] = useChangePasswordMutation();

    const [name, setName] = useState(profile.NAME);
    const [firstName, setFirstName] = useState(profile.FIRSTNAME);
    const [sexe, setSexe] = useState(profile.SEXE ?? '');
    const [birthday, setBirthday] = useState<Dayjs>(profile.BIRTHDAY ? dayjs(profile.BIRTHDAY) : dayjs(new Date()));
    const [phone, setPhone] = useState(profile.PHONE ?? '');
    const [receiveWeeklyRecap, setReceiveWeeklyRecap] = useState(!profile.WEEKLY_RECAP_OPT_OUT);
    const [profileSnackbar, setProfileSnackbar] = useState<'success' | 'error' | null>(null);

    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [passwordSnackbar, setPasswordSnackbar] = useState<'success' | null>(null);

    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        const birthdayString = birthday.toISOString().slice(0, 19).replace('T', ' ');

        try {
            await updateMyProfile({
                NAME: name,
                FIRSTNAME: firstName,
                PHONE: phone,
                SEXE: sexe,
                BIRTHDAY: birthdayString,
                WEEKLY_RECAP_OPT_OUT: !receiveWeeklyRecap,
            }).unwrap();
            setProfileSnackbar('success');
        } catch (error) {
            setProfileSnackbar('error');
        }
    };

    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            setPasswordError(t('signup.passwordMismatch'));
            return;
        }

        try {
            await changePassword({ currentPassword, newPassword }).unwrap();
            setPasswordError('');
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setPasswordSnackbar('success');
        } catch (error) {
            // The backend only ever returns one error here (wrong current
            // password), and its message isn't localized -- always show the
            // translated string rather than the raw English backend text.
            setPasswordError(t('profile.incorrectPassword'));
        }
    };

    return (
        <>
            <Paper sx={{ p: 3, mb: 3 }}>
                <form onSubmit={handleSaveProfile}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField id="profile-name" label={t('signup.lastName')} fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField id="profile-firstName" label={t('signup.firstName')} fullWidth required value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel htmlFor="profile-sexe">{t('signup.sexe')}</InputLabel>
                                <Select label={t('signup.sexe')} id="profile-sexe" value={sexe} onChange={(e) => setSexe(e.target.value)}>
                                    <MenuItem value="Homme">{t('signup.male')}</MenuItem>
                                    <MenuItem value="Femme">{t('signup.female')}</MenuItem>
                                    <MenuItem value="Autre">{t('signup.other')}</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language} localeText={pickersLocaleText}>
                                <DatePicker
                                    label={t('signup.birthday')}
                                    format="DD-MM-YYYY"
                                    value={birthday}
                                    onChange={(value: Dayjs | null) => value && setBirthday(value)}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label={t('signup.email')} fullWidth value={profile.EMAIL} disabled />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <ReactPhoneInput label={t('signup.mobile')} variant="outlined" country="fr" value={phone} onChange={setPhone} enableSearch component={TextField} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={receiveWeeklyRecap} onChange={(e) => setReceiveWeeklyRecap(e.target.checked)} />}
                                label={t('profile.weeklyRecapLabel')}
                            />
                            <Typography variant="body2" color="text.secondary">
                                {t('profile.weeklyRecapDescription')}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" color="primary" type="submit">
                                {t('profile.saveChanges')}
                            </Button>
                        </Grid>
                    </Grid>
                </form>
            </Paper>

            {profile.HAS_PASSWORD === 1 && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        {t('profile.changePasswordTitle')}
                    </Typography>
                    <form onSubmit={handleChangePassword}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    id="profile-current-password"
                                    type="password"
                                    label={t('profile.currentPassword')}
                                    fullWidth
                                    required
                                    value={currentPassword}
                                    onChange={(e) => setCurrentPassword(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="profile-new-password"
                                    type="password"
                                    label={t('signup.password')}
                                    fullWidth
                                    required
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    id="profile-confirm-password"
                                    type="password"
                                    label={t('signup.confirmPassword')}
                                    fullWidth
                                    required
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    error={passwordError !== ''}
                                    helperText={passwordError}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button variant="contained" color="primary" type="submit">
                                    {t('profile.changePasswordTitle')}
                                </Button>
                            </Grid>
                        </Grid>
                    </form>
                </Paper>
            )}

            <Snackbar open={profileSnackbar !== null} autoHideDuration={4000} onClose={() => setProfileSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setProfileSnackbar(null)} severity={profileSnackbar ?? 'success'} variant="filled">
                    {profileSnackbar === 'success' ? t('profile.profileUpdated') : t('common.genericError')}
                </Alert>
            </Snackbar>

            <Snackbar open={passwordSnackbar !== null} autoHideDuration={4000} onClose={() => setPasswordSnackbar(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setPasswordSnackbar(null)} severity="success" variant="filled">
                    {t('profile.passwordChanged')}
                </Alert>
            </Snackbar>
        </>
    );
}

function Profile() {
    const { t } = useTranslation();
    const { data, isLoading } = useGetMyProfileQuery();

    return (
        <Container maxWidth="sm" sx={{ py: 4 }}>
            <Typography variant="h4" gutterBottom>
                {t('profile.title')}
            </Typography>
            {isLoading || !data ? (
                <Box>
                    <Skeleton variant="rectangular" height={300} sx={{ mb: 3 }} />
                    <Skeleton variant="rectangular" height={200} />
                </Box>
            ) : (
                <ProfileForm profile={data} />
            )}
        </Container>
    );
}

export default Profile;
